import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, RefreshCw, Sparkles, X } from 'lucide-react';
import { errMsg } from '../../lib/aiInterviewApi';
import { COVERAGE_TONE, KIND_LABEL, roomAPI } from '../../lib/roomApi';
import { Pill } from '../aiinterview/recruiter/common';

const POLL_MS = 5000;
const DOT = { on: 'green', muted: 'amber', standby: 'slate', waiting: 'slate', no_consent: 'red', off: 'slate' };
const CAND_TEXT = { on: 'Listening', muted: 'Muted', standby: 'Another interviewer is capturing', waiting: 'Not in the call yet', no_consent: 'No consent to transcribe', off: 'Off' };

const Suggestion = ({ s, onMark }) => (
  <div className={`vr-sugg ${s.status !== 'new' ? 'is-done' : ''}`} data-testid={`copilot-suggestion-${s.id}`}>
    <div className="iv-row" style={{ gap: '0.35rem' }}>
      <Pill tone={s.kind === 'verify_claim' ? 'amber' : s.kind === 'gap' ? 'sky' : 'teal'}>{KIND_LABEL[s.kind] || 'Follow-up'}</Pill>
      {s.competency && s.competency !== 'General' && <span className="iv-small">{s.competency}</span>}
      {s.source === 'ai_report' && <span className="iv-small">· from AI interview</span>}
    </div>
    <p className="vr-sugg-q">{s.text}</p>
    {s.why && <p className="iv-small" style={{ margin: 0 }}>{s.why}</p>}
    {s.status === 'new' ? (
      <div className="iv-row" style={{ marginTop: '0.5rem' }}>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => onMark(s, 'asked')} data-testid={`copilot-asked-${s.id}`}><Check size={14} /> Asked</button>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => onMark(s, 'dismissed')} data-testid={`copilot-dismiss-${s.id}`}><X size={14} /> Dismiss</button>
      </div>
    ) : <button type="button" className="vr-link" onClick={() => onMark(s, 'new')} data-testid={`copilot-restore-${s.id}`}>{s.status === 'asked' ? 'Asked' : 'Dismissed'} · undo</button>}
  </div>
);

const Transcript = ({ lines }) => {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [lines.length]);
  if (!lines.length) return <p className="iv-small" data-testid="copilot-transcript-empty">The live transcript appears here once people start talking.</p>;
  return (
    <div className="vr-transcript" data-testid="copilot-transcript">
      {lines.map((l) => (
        <div key={l.seq} className={`iv-turn ${l.role === 'candidate' ? 'is-cand' : ''}`} style={{ cursor: 'default' }}>
          <time>{new Date(l.at.endsWith('Z') ? l.at : `${l.at}Z`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
          <div><b>{l.speaker}</b><p>{l.text}</p></div>
        </div>
      ))}
      <div ref={end} />
    </div>
  );
};

export default function CopilotPanel({ bookingId, capture, live, onConsent }) {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const after = useRef(0);
  const load = useCallback(async () => {
    try {
      const { data: d } = await roomAPI.copilot(bookingId, after.current);
      if (d.transcript.length) {
        after.current = Math.max(after.current, d.transcript[d.transcript.length - 1].seq);
        setLines((x) => {
          const seen = new Set(x.map((l) => l.seq));
          return [...x, ...d.transcript.filter((l) => !seen.has(l.seq))];
        });
      }
      setData(d);
      onConsent?.(d.candidate_consent);
    } catch (e) { /* keep last state; next poll retries */ }
  }, [bookingId, onConsent]);
  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);
  const mark = async (s, status) => {
    setData((d) => ({ ...d, suggestions: d.suggestions.map((x) => (x.id === s.id ? { ...x, status } : x)) }));
    try { await roomAPI.markSuggestion(bookingId, s.id, status); } catch (e) { toast.error(errMsg(e, 'Could not update')); load(); }
  };
  const suggest = async () => {
    setBusy(true);
    try { await roomAPI.suggest(bookingId); toast.success('Fresh suggestions are on the way'); setTimeout(load, 6000); } catch (e) { toast.error(errMsg(e, 'Could not get suggestions')); } finally { setBusy(false); }
  };
  if (!data) return <p className="iv-small">Loading copilot…</p>;
  const open = data.suggestions.filter((s) => s.status === 'new');
  const done = data.suggestions.filter((s) => s.status !== 'new');
  return (
    <div className="vr-panel-body" data-testid="copilot-panel">
      {live && (
        <div className="vr-listen" data-testid="copilot-listen-status">
          <span><i className={`vr-dot is-${DOT[capture.self]}`} />You: {capture.self === 'on' ? 'Listening' : capture.self === 'muted' ? 'Muted' : 'Off'}</span>
          <span><i className={`vr-dot is-${DOT[capture.candidate]}`} />Candidate: {CAND_TEXT[capture.candidate]}</span>
        </div>
      )}
      <div className="iv-row" style={{ justifyContent: 'space-between' }}>
        <span className="iv-eyebrow"><Sparkles size={12} /> Suggested next questions</span>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" disabled={busy || data.generating} onClick={suggest} data-testid="copilot-suggest-btn">
          <RefreshCw size={14} className={data.generating ? 'vr-spin' : ''} /> {data.generating ? 'Thinking…' : 'Suggest now'}
        </button>
      </div>
      {data.flags.map((f) => <div key={f} className="iv-note is-warn" data-testid="copilot-flag">{f}</div>)}
      {!open.length && <p className="iv-small" data-testid="copilot-empty">No open suggestions. New ones appear about once a minute while the candidate speaks.</p>}
      {open.slice(0, 8).map((s) => <Suggestion key={s.id} s={s} onMark={mark} />)}
      {done.length > 0 && <button type="button" className="vr-link" onClick={() => setShowDone((v) => !v)} data-testid="copilot-done-toggle">{showDone ? 'Hide' : 'Show'} asked / dismissed ({done.length})</button>}
      {showDone && done.map((s) => <Suggestion key={s.id} s={s} onMark={mark} />)}
      {Object.keys(data.coverage).length > 0 && (
        <div data-testid="copilot-coverage">
          <div className="iv-eyebrow" style={{ marginBottom: '0.4rem' }}>Competency coverage</div>
          <div className="iv-row" style={{ gap: '0.35rem' }}>
            {Object.entries(data.coverage).map(([k, v]) => <Pill key={k} tone={COVERAGE_TONE[v]} title={v.replace('_', ' ')}>{k}: {v === 'not_yet' ? 'not yet' : v}</Pill>)}
          </div>
        </div>
      )}
      <div>
        <div className="iv-eyebrow" style={{ marginBottom: '0.4rem' }}>Live transcript</div>
        <Transcript lines={lines} />
      </div>
    </div>
  );
}
